/**
 * Product analytics — the four events that answer "did the traffic convert?".
 *
 * haevi.ai ran with no instrumentation of any kind through its first ~800
 * social impressions, so every one of those visits is unattributable forever.
 * This module exists to stop that, and it is deliberately small: four explicit
 * events on the acquisition funnel, and nothing else.
 *
 *     page_view  →  gate_hit  →  signin_started  →  signed_in
 *
 * Those map onto the real flow: a stranger lands on "/", types into the
 * Composer, submits without a session (Composer stashes the prompt and
 * navigates to /login), picks a sign-in method, and comes back authenticated.
 *
 * ## Why a plain POST and not posthog-js
 *
 * PostHog's capture endpoint takes JSON over HTTP, so the SDK buys us
 * autocapture and session replay we do not want here: autocapture on a
 * ReactFlow canvas is thousands of meaningless click events, and replay on a
 * product where users type creative prompts is a privacy liability. Four
 * hand-placed events are the entire requirement. Skipping the dependency also
 * keeps a third-party script off the page, so there is no new CSP surface and
 * no bundle cost.
 *
 * ## Why first-touch attribution
 *
 * Attribution is captured on the FIRST visit that carries UTM params and then
 * frozen in localStorage. This is not a preference — last-touch is actively
 * wrong for this funnel. Signing in with Google navigates to
 * accounts.google.com and back, and the magic-link flow arrives from a mail
 * client. Under last-touch, every TikTok visitor who converts is recorded as
 * having come from google.com, which is precisely backwards: the conversions
 * get credited to the channel that did none of the work.
 */

type Primitive = string | number | boolean | null

/** The acquisition funnel, in order. Nothing else belongs in this union. */
export type AnalyticsEvent =
  | 'page_view'
  | 'gate_hit'
  | 'signin_started'
  | 'signed_in'

interface Attribution {
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_content?: string
  utm_term?: string
  referrer?: string
  landing_path?: string
  captured_at?: string
}

const DISTINCT_ID_KEY = 'haevi.analytics.distinct_id'
const ATTRIBUTION_KEY = 'haevi.analytics.attribution'
const IDENTIFIED_KEY = 'haevi.analytics.identified_as'

const UTM_KEYS = [
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term',
] as const

/**
 * Read config per call rather than at module scope.
 *
 * Module-level `const KEY = import.meta.env...` is evaluated at import time,
 * which makes the disabled path untestable — `vi.stubEnv` in a test runs after
 * the import has already frozen the value.
 */
function config(): { key: string; host: string } | null {
  const key = import.meta.env.VITE_POSTHOG_KEY as string | undefined
  if (!key) return null
  const host = (import.meta.env.VITE_POSTHOG_HOST as string | undefined)
    ?? 'https://us.i.posthog.com'
  return { key, host: host.replace(/\/$/, '') }
}

/** Honour Do Not Track. Cheap to respect, and the right default for a consumer product. */
function optedOut(): boolean {
  if (typeof navigator === 'undefined') return false
  const dnt = (navigator as Navigator & { msDoNotTrack?: string }).msDoNotTrack
    ?? navigator.doNotTrack
    ?? (typeof window !== 'undefined'
      ? (window as Window & { doNotTrack?: string }).doNotTrack
      : undefined)
  return dnt === '1' || dnt === 'yes'
}

function readStored(key: string): string | null {
  try {
    return window.localStorage.getItem(key)
  } catch {
    // Private browsing / storage disabled. Analytics degrades to per-pageview
    // anonymity rather than throwing into the caller's render path.
    return null
  }
}

function writeStored(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value)
  } catch { /* see readStored */ }
}

function distinctId(): string {
  const existing = readStored(DISTINCT_ID_KEY)
  if (existing) return existing
  const fresh = typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `anon-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`
  writeStored(DISTINCT_ID_KEY, fresh)
  return fresh
}

/**
 * Freeze the first UTM-bearing visit. Later visits never overwrite it — see
 * the first-touch note in the module docstring.
 */
function captureAttribution(): Attribution {
  const stored = readStored(ATTRIBUTION_KEY)
  if (stored) {
    try {
      return JSON.parse(stored) as Attribution
    } catch { /* corrupt value: fall through and re-capture */ }
  }

  const params = new URLSearchParams(window.location.search)
  const next: Attribution = {}
  for (const k of UTM_KEYS) {
    const v = params.get(k)
    if (v) next[k] = v.slice(0, 120)
  }

  // A visit with no UTMs is not worth freezing as "the first touch" — doing so
  // would lock in `{}` for a user who later arrives on a real campaign link.
  if (Object.keys(next).length === 0) return {}

  next.referrer = document.referrer || undefined
  next.landing_path = window.location.pathname
  next.captured_at = new Date().toISOString()
  writeStored(ATTRIBUTION_KEY, JSON.stringify(next))
  return next
}

function currentAttribution(): Attribution {
  const stored = readStored(ATTRIBUTION_KEY)
  if (!stored) return {}
  try {
    return JSON.parse(stored) as Attribution
  } catch {
    return {}
  }
}

function send(body: Record<string, unknown>, host: string): void {
  const url = `${host}/i/v0/e/`
  const json = JSON.stringify(body)
  try {
    // sendBeacon survives the page teardown that follows `gate_hit` — that
    // event fires immediately before a navigate() away from the landing page,
    // and a plain fetch() is cancelled on unload often enough to put a hole
    // in the middle of the funnel.
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const ok = navigator.sendBeacon(url, new Blob([json], { type: 'application/json' }))
      if (ok) return
    }
    void fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: json,
      keepalive: true,
      mode: 'cors',
    }).catch(() => { /* analytics must never surface an error to the user */ })
  } catch { /* ditto */ }
}

/** Emit one funnel event. No-ops when unconfigured or opted out. */
export function track(event: AnalyticsEvent, props: Record<string, Primitive> = {}): void {
  if (typeof window === 'undefined') return
  const cfg = config()
  if (!cfg || optedOut()) return
  send({
    api_key: cfg.key,
    event,
    distinct_id: distinctId(),
    timestamp: new Date().toISOString(),
    properties: {
      ...currentAttribution(),
      ...props,
      $current_url: window.location.href,
      $pathname: window.location.pathname,
    },
  }, cfg.host)
}

/**
 * Bind the anonymous journey to a real user.
 *
 * Without the `$anon_distinct_id` alias, the pre-sign-in events and the
 * post-sign-in events belong to two different people as far as PostHog is
 * concerned, and every funnel from `page_view` to `signed_in` reports zero
 * conversions no matter how many there were.
 *
 * Returns true only on the FIRST identification of this user in this browser.
 * Callers use that to fire the conversion event, because Firebase's auth
 * observer also fires on every page load and token refresh for an
 * already-signed-in user — counting those would report a conversion every time
 * a existing user opened the site.
 */
export function identify(userId: string): boolean {
  if (typeof window === 'undefined') return false
  const cfg = config()
  if (!cfg || optedOut()) return false
  if (readStored(IDENTIFIED_KEY) === userId) return false

  const anonId = distinctId()
  send({
    api_key: cfg.key,
    event: '$identify',
    distinct_id: userId,
    timestamp: new Date().toISOString(),
    properties: { $anon_distinct_id: anonId, ...currentAttribution() },
  }, cfg.host)

  writeStored(IDENTIFIED_KEY, userId)
  writeStored(DISTINCT_ID_KEY, userId)
  return true
}

/**
 * Drop the signed-in identity on sign-out.
 *
 * `identify` pins distinct_id to the uid permanently. Without this, a shared
 * or handed-over browser attributes the NEXT person's anonymous browsing to
 * whoever signed in last, until they sign in themselves. Attribution is
 * deliberately kept: first touch belongs to the browser, not the account.
 */
export function resetIdentity(): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.removeItem(IDENTIFIED_KEY)
    window.localStorage.removeItem(DISTINCT_ID_KEY)
  } catch { /* see readStored */ }
}

/**
 * Capture first-touch attribution and emit the entry `page_view`.
 * Call once, from main.tsx, before render.
 */
export function initAnalytics(): void {
  if (typeof window === 'undefined') return
  if (!config()) {
    // Loud enough to notice in dev, silent in production. A build shipped
    // without the key looks identical to a working one at runtime, and that
    // is exactly how a site ends up with no data for a fortnight.
    if (import.meta.env.DEV) {
      console.info('[analytics] VITE_POSTHOG_KEY is unset — no events will be sent.')
    }
    return
  }
  captureAttribution()
  track('page_view')
}
